import React from 'react';
import {
  DOC_TYPE_INFORMATION,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_FORM,
  DOC_TYPE_PDF_FORM,
  DOC_TYPE_ID_CHECK,
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_PAYMENT,
  isSkipDocumentListMode,
  mapToArray,
  collectPartialCompleteDocuments,
  findNotReadyDocumentInList,
  hasAnyDocumentInputChanges
} from './utils';
import { gaClickOnDocType, gaeList } from '../requestedDocuments/ga';
import { f7navigator } from './navigator';
import { gae, gaeApp, gaeUserNavigate, toQualifiedAction } from './Analytics';
import {
  beginJourney,
  JOURNEY,
  beginDocument,
  endDocument
} from '../../telemetry/tracing';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { selectPreparedState } from '../../redux/selectors/preparedState';
import {
  setCurrentRequestPageIndex,
  showRejectDocumentDialog,
  startSendRequest,
  setSendRequestTask,
  setCreateTemplateRequestTask,
  setUserCredentialsTask,
  setUsersData,
  setDocumentForm
} from '../../redux/actions/rootAction';
import II18 from '../translate/II18';
import { STARTED_FROM_QR } from './withAppStart';
import { gaNavigateQrCode, gaNavigateToList } from '../home/ga';
import { isRequestACrossDeviceDocument } from '../../lib/utils';
import { alertDialog, confirmDialog } from './dialogUtils';
import {
  clearExternalApiValidationStamp,
  getExternalApiValidationConfig,
  runExternalApiValidation,
  withExternalApiValidationStamp
} from './externalApiValidation';
import {
  buildUsersData,
  applyCustomRecipientsData,
  usersDataAllOk,
  getPreparedUsersDataInfo
} from './userFieldsData';
import { canTriggerPartialSendOnPageExit } from '../../redux/utils';
import {
  createSendRequestTask,
  isSendRequestProcessing,
  SEND_REQUEST_STATUS
} from '../../redux/sendRequestTask';
import {
  createCreateTemplateRequestTask,
  CREATE_TEMPLATE_REQUEST_STATUS,
  isCreateTemplateRequestActive
} from '../../redux/createTemplateRequestTask';
import {
  createUserCredentialsTask,
  USER_CREDENTIALS_STATUS
} from '../../redux/userCredentialsTask';
import {
  flushScheduledStandaloneDraftSave,
  isStandaloneDraftCachedForTemplate
} from '../../lib/standaloneDraftCache';
import { isAppOnline, prepareApp } from '../../lib/utils';
import { f7 } from 'framework7-react';

export default function withDocumentRootLogic(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class withDocumentRootLogicWrapper extends React.Component {
    state = {
      id: Math.random()
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (
        this.state.waitPageCompleted !== undefined &&
        this.props.requestPages[this.state.waitPageCompleted].submitted
      ) {
        const navigateToPage = this.state.waitPageCompleted + 1;
        gaeList('NAVIGATE_AFTER_COMPLETE_PAGE_DONE', navigateToPage);
        this.setState(
          {
            waitPageCompleted: undefined
          },
          () => this.goToRequestPage(navigateToPage)
        );
      }
    }

    setCurrentActionDocument(
      currentActionForDocType,
      currentActionForDocId,
      props
    ) {
      gaeApp(
        toQualifiedAction(
          'SET_CURRENT_ACTION_DOCUMENT',
          currentActionForDocType || 'CLEAR'
        )
      );

      // Desktop dynamic/stepper (and multi-doc popups): one `document` span per
      // open doc. Jumping to another doc ends the previous via beginDocument.
      if (currentActionForDocType && currentActionForDocId) {
        const doc = this.getRequestedDocumentById(currentActionForDocId);
        beginDocument({
          'document.type': currentActionForDocType,
          'document.id': currentActionForDocId,
          'document.optional': Boolean(doc && doc.optional),
          'document.ui': 'desktop_in_page'
        });
      } else {
        endDocument('ok', { 'document.end_reason': 'clear_in_page' });
      }

      this.setState({
        currentActionForDocType: currentActionForDocType,
        currentActionForDocId: currentActionForDocId,
        currentActionForDocProps: props
      });
    }

    getRequestedDocumentById = id =>
      this.props.requestedDocuments.find(d => d.id === id);

    removeCurrentActionDocument = () => {
      const currentDocument = this.getRequestedDocumentById(
        this.state.currentActionForDocId
      );

      if (currentDocument) {
        gaeApp(
          'DOCUMENT_LEAVE_IN_STEPPER_STATS',
          JSON.stringify(currentDocument.stats, null, 2)
        );
      }
      this.setCurrentActionDocument(null, null);
    };

    needRedirectToSmartFill = document => {
      return document.smartFill && !this.props.scanEnded && !document.submitted;
    };

    navigateToInPageDocument = (document, props) => {
      gaClickOnDocType(document.type, document.id.substring(0, 4));
      this.setCurrentActionDocument(document.type, document.id, props);
    };

    getCurrentActionDocument = () => {
      return (
        this.state.currentActionForDocId &&
        this.getRequestedDocumentById(this.state.currentActionForDocId)
      );
    };

    isRequestPageNeedToComplete = pageIndex => {
      const { requestPages } = this.props;

      // Last page is always sent with the final request completion.
      if (!canTriggerPartialSendOnPageExit(pageIndex, requestPages)) {
        return false;
      }

      //double check that there some document not yet submitted
      return requestPages
        .filter((page, index) => index <= pageIndex)
        .some(page =>
          page.documents.some(
            doc =>
              this.getRequestedDocumentById(doc.id)?.submitted === false &&
              this.getRequestedDocumentById(doc.id)?.optional === false
          )
        );
    };

    completePage = (n, fromDocView) => {
      this.setState({
        waitPageCompleted: n
      });
      this.onSendRequest(this.props.requestPages[n].pageId, fromDocView);
    };

    /**
     * Re-run external-api/validate for done forms before send.
     * Safety net when stamp was restored from draft or API rules changed.
     * Does not touch partial-complete / page scoping — only docs already marked done.
     * @returns {Promise<boolean>} false → abort send
     */
    ensureExternalApiValidationBeforeSend = async () => {
      const {
        requestedDocuments,
        preparedFormsStructures,
        forms,
        recipientRequestId,
        templateMode,
        setDocumentForm: setDocumentFormAction
      } = this.props;

      const docsToValidate = requestedDocuments.filter(
        doc =>
          (doc.type === DOC_TYPE_FORM || doc.type === DOC_TYPE_PDF_FORM) &&
          getExternalApiValidationConfig(doc) &&
          !doc.rejected &&
          !doc.submitted &&
          doc.done
      );

      if (!docsToValidate.length) {
        return true;
      }

      gaeApp(
        'SEND_REQUEST.EXT_VALIDATION',
        `${docsToValidate.length} document(s)`
      );
      f7.dialog.preloader(II18.get('forms_external_validation_wait'));

      try {
        for (const doc of docsToValidate) {
          const formStructure =
            preparedFormsStructures?.get(doc.id) || doc.formStructure;
          const formData = forms?.get(doc.id) || new Map();

          const result = await runExternalApiValidation({
            document: doc,
            formStructure,
            formData,
            recipientRequestId,
            templateMode,
            onEndpointStart: (externalApiId, customIds) => {
              gae(
                'FORMS',
                'VALIDATION_API',
                externalApiId,
                `fieldsCount: ${customIds?.length}`
              );
            }
          });

          if (result.apiFailure) {
            gaeApp('SEND_REQUEST.EXT_VALIDATION_API_ERROR', doc.id);
            alertDialog(II18.get('forms_external_validation_error'));
            return false;
          }

          if (!result.ok) {
            gaeApp('SEND_REQUEST.EXT_VALIDATION_FAILED', doc.id);
            setDocumentFormAction(
              doc.id,
              clearExternalApiValidationStamp(formData)
            );
            this.showAlertDocumentNotReady(doc);
            return false;
          }

          setDocumentFormAction(
            doc.id,
            withExternalApiValidationStamp(formData, result.stamp)
          );
        }
      } finally {
        f7.dialog.close();
      }

      return true;
    };

    /**
     * Single entry for the send process.
     * fromDocView=true → same path as list/stepper, with doc-view send options.
     */
    onSendRequest = (tillPage, fromDocView, sendOptions = {}) => {
      const {
        scanEnded,
        sendRequestTask,
        templateMode,
        startSendRequest: startSendRequestAction,
        setSendRequestTask: setSendRequestTaskAction
      } = this.props;

      if (scanEnded) {
        gaeApp('NO_SEND_FILES_AFTER_SCAN_ENDED');
        return;
      }

      if (!isAppOnline()) {
        this.$f7.dialog.alert(
          II18.get('offline_template_submit'),
          II18.get('common_error')
        );
        return;
      }

      if (isSendRequestProcessing(sendRequestTask)) {
        gaeApp('CANCEL_SECOND_TRY_TO_SEND_FILES');
        return;
      }

      const proceed = () => {
        //remove previous pages in history (list / stepper)
        this.$f7router.clearPreviousHistory();

        const { noProgressDialog = false, skipThankyou = false } = sendOptions;
        const sendPayload = {
          fromDocView: Boolean(fromDocView),
          tillPage,
          noProgressDialog,
          skipThankyou
        };

        beginJourney(JOURNEY.SUBMIT, 'send.process', {
          'submit.mode': tillPage ? 'partial' : 'full',
          'submit.from_doc_view': Boolean(fromDocView),
          'submit.template_prepare': Boolean(templateMode),
          'submit.has_till_page': Boolean(tillPage)
        });

        if (templateMode) {
          gaeApp(
            'SEND_REQUEST.PREPARE_TEMPLATE',
            `fromDocView: ${fromDocView}; completePageId: ${tillPage}`
          );
          beginJourney(JOURNEY.PREPARE, 'prepare.process', {
            'prepare.from_template': true
          });
          // Send owns "waiting for template"; create-template does not know about send.
          setSendRequestTaskAction(
            createSendRequestTask({
              status: SEND_REQUEST_STATUS.PREPARING_TEMPLATE,
              ...sendPayload
            })
          );
          this.startCreateTemplateRequest();
        } else {
          gaeApp(
            'SEND_REQUEST.BEGIN',
            `fromDocView: ${fromDocView}; completePageId: ${tillPage}`
          );
          startSendRequestAction(sendPayload);
        }
      };

      const startAfterExtValidation = async () => {
        const extOk = await this.ensureExternalApiValidationBeforeSend();
        if (!extOk) {
          return;
        }

        if (!window.appPreparedOnline) {
          prepareApp(proceed, () => {
            this.$f7.dialog.alert(
              II18.get('technical_error'),
              II18.get('common_error')
            );
          });
          return;
        }

        proceed();
      };

      startAfterExtValidation();
    };

    doSendRequestFromDocumentView = (sendOptions = {}) => {
      this.onSendRequest(undefined, true, sendOptions);
    };

    /**
     * Create-request-from-template only.
     * If users data incomplete → pause prepare and start credentials process (V2).
     */
    startCreateTemplateRequest = ({
      customRecipientsDataSource,
      customTitle,
      skipPreparingDialog,
      embedInCaller
    } = {}) => {
      const {
        createTemplateRequestTask,
        setUsersData: setUsersDataAction,
        setCreateTemplateRequestTask: setCreateTemplateRequestTaskAction,
        setUserCredentialsTask: setUserCredentialsTaskAction
      } = this.props;

      if (isCreateTemplateRequestActive(createTemplateRequestTask)) {
        gaeApp('CREATE_TEMPLATE_REQUEST_ABORT.PROCESS_ALREADY_ACTIVE');
        return;
      }

      const preparedUsersData = applyCustomRecipientsData(
        this.props.usersData,
        customRecipientsDataSource
      );

      gaeApp(
        'PREPARED_USERS_DATA_INFO',
        getPreparedUsersDataInfo(preparedUsersData)
      );

      if (!usersDataAllOk(preparedUsersData)) {
        gaeApp(
          'CREATE_TEMPLATE_REQUEST_AWAITING_CREDENTIALS.NOT_ALL_USERS_DATA'
        );
        beginJourney(JOURNEY.CREDENTIALS, 'credentials.collect', {
          'credentials.reason': 'incomplete_users_data'
        });
        setCreateTemplateRequestTaskAction(
          createCreateTemplateRequestTask({
            status: CREATE_TEMPLATE_REQUEST_STATUS.AWAITING_CREDENTIALS,
            skipPreparingDialog
          })
        );
        setUserCredentialsTaskAction(
          createUserCredentialsTask({
            status: USER_CREDENTIALS_STATUS.PROCESSING,
            customRecipientsDataSource,
            customTitle,
            embedInCaller
          })
        );
      } else {
        gaeApp('CREATE_TEMPLATE_REQUEST_PREPARING');
        setUsersDataAction(preparedUsersData);
        setCreateTemplateRequestTaskAction(
          createCreateTemplateRequestTask({
            status: CREATE_TEMPLATE_REQUEST_STATUS.PREPARING,
            usersData: preparedUsersData,
            skipPreparingDialog
          })
        );
      }
    };

    showAlertDocumentNotReady = errDoc => {
      this.$f7.dialog.alert(
        II18.get('dialog_required_document_' + errDoc.type, errDoc.name),
        II18.get('dialog_attention')
      );
    };

    goToRequestPage = (n, fromDocView) => {
      if (n > this.props.currentRequestPageIndex) {
        for (
          let pageIdx = this.props.currentRequestPageIndex;
          pageIdx < n;
          pageIdx++
        ) {
          const errDoc = findNotReadyDocumentInList(
            this.props.requestPages[pageIdx]?.documents
          );
          if (errDoc) {
            gaeList('SHOW_ALERT', 'DIALOG_LOAD_ALL_REQUIRED');
            gaeApp('GO_TO_REQUEST_PAGE_BLOCKED', errDoc.name);
            this.showAlertDocumentNotReady(errDoc);
            return;
          }
        }
      }

      if (n > 0 && this.isRequestPageNeedToComplete(n - 1)) {
        gaeList(
          'SHOW_COMPLETE_PAGE_CONFIRMATION',
          `${this.props.requestPages[n - 1].title} (${
            this.props.requestPages[n - 1].pageId
          })`
        );
        const { documentsToSend } = collectPartialCompleteDocuments(
          this.props.requestPages[n - 1].pageId,
          this.props.requestPages,
          this.props.requestedDocuments
        );
        gaeList(
          'DOCUMENTS_TO_SEND_ONLY',
          JSON.stringify(documentsToSend?.map(doc => doc.name))
        );
        confirmDialog(
          II18.get('complete_page_title'),
          II18.get('complete_page_message', this.props.companyName),
          () => this.completePage(n - 1, fromDocView),
          null,
          II18.get('complete_page_perform')
        );
      } else {
        const summaryStep = this.props.requestPages[n].summaryStep;
        if (
          this.getCurrentActionDocument() &&
          (this.props.requestPages[this.props.currentRequestPageIndex].documents
            .length > 1 ||
            n !== this.props.currentRequestPageIndex ||
            summaryStep)
        ) {
          this.removeCurrentActionDocument();
        }

        gaeList(
          'GO_TO_REQUEST_PAGE',
          `${n + 1}: ${this.props.requestPages[n].title}`
        );
        this.props.setCurrentRequestPageIndex(n);

        if (!window.isDesktop) {
          this.startMobileNextRequestPage(n);
        }
      }
    };

    startMobileNextRequestPage = navigateToStep => {
      gaeUserNavigate('MOBILE_START_REQUEST_PAGE', navigateToStep);

      const navigateToLastStep =
        navigateToStep === this.props.requestPages.length - 1;

      const navigateToManyDocumentsStep =
        !navigateToLastStep &&
        this.props.requestPages[navigateToStep].documents.length > 1;

      if (navigateToLastStep || navigateToManyDocumentsStep) {
        f7navigator.toList();
      } else {
        this.navigateToMobileDocument(
          this.props.requestPages[navigateToStep].documents[0]
        );
      }
    };

    navigateToRequestNextPageFromMobileDocument = () => {
      const navigateToStep = this.props.currentRequestPageIndex + 1;
      gaeUserNavigate('MOBILE_GO_TO_REQUEST_NEXT_PAGE', navigateToStep);
      const currentStepIsManyDocuments =
        this.props.requestPages[this.props.currentRequestPageIndex].documents
          .length > 1;

      if (currentStepIsManyDocuments) {
        f7navigator.toList();
      } else {
        this.goToRequestPage(navigateToStep, true);
      }
    };

    notifyParentNavigateBack = () => {
      gaeList('POST_MESSAGE', 'PWA_NAVIGATE_BACK');
      window.parent.postMessage('PWA_NAVIGATE_BACK', '*');
    };

    /**
     * Embed forces skipStartPage=true, but skipStartPageOriginal preserves
     * whether the request actually has a start/home page (shown inside the
     * desktop stepper as StartContent). Parent exit is only valid when there
     * is no such page.
     */
    canExitEmbedToParent = () =>
      Boolean(this.props.embedMode && this.props.skipStartPageOriginal);

    navigateToRequestPrevPageFromMobileDocument = fromList => {
      gaeUserNavigate(
        'Prev stepper page',
        this.props.currentRequestPageIndex - 1
      );
      const stayOnSameStep =
        !fromList &&
        this.props.requestPages[this.props.currentRequestPageIndex].documents
          .length > 1;
      const firstStep = this.props.currentRequestPageIndex === 0;
      const prevStepIsOneDocument =
        !firstStep &&
        this.props.requestPages[this.props.currentRequestPageIndex - 1]
          .documents.length === 1;

      if (firstStep && !stayOnSameStep) {
        if (this.canExitEmbedToParent()) {
          this.notifyParentNavigateBack();
          return;
        }
        if (!this.props.scanEnded) {
          this.navigateToHome();
          return;
        }
      }
      if (
        stayOnSameStep ||
        !prevStepIsOneDocument ||
        (firstStep && this.props.scanEnded)
      ) {
        endDocument('ok', { 'document.end_reason': 'back_to_list' });
        f7navigator.forceBackToList();
      } else {
        this.navigateToMobileDocument(
          this.props.requestPages[this.props.currentRequestPageIndex - 1]
            .documents[0],
          true
        );
      }
      if (!stayOnSameStep && !firstStep) {
        this.props.setCurrentRequestPageIndex(
          this.props.currentRequestPageIndex - 1
        );
      }
    };

    navigateToDesktopDocument = document => {
      gaClickOnDocType(document.type, document.id.substring(0, 4));
      const docType = document.type;

      // In-page popup: setCurrentActionDocument owns beginDocument (closes prior
      // doc span on jump). Full-page routes begin below.
      if (
        this.props.requestedDocuments.length > 1 &&
        [DOC_TYPE_PAYMENT, DOC_TYPE_ID_CHECK, DOC_TYPE_DOCUMENT].includes(
          docType
        )
      ) {
        this.setCurrentActionDocument(docType, document.id);
        return;
      }

      beginDocument({
        'document.type': document.type,
        'document.id': document.id,
        'document.optional': Boolean(document.optional),
        'document.ui': 'desktop'
      });

      switch (docType) {
        case DOC_TYPE_PAYMENT:
          f7navigator.toPayment(document.id, null, { animate: false });
          break;
        case DOC_TYPE_ID_CHECK:
          f7navigator.toIdCheck(document.id, null, { animate: false });
          break;
        case DOC_TYPE_DOCUMENT:
          f7navigator.toGallery(document.id, null, { animate: false });
          break;
        case DOC_TYPE_FORM:
          f7navigator.toQuickForm(document.id, null, { animate: false });
          break;
        case DOC_TYPE_PDF_FORM:
          if (this.needRedirectToSmartFill(document)) {
            f7navigator.toQuickForm(
              document.id,
              { smartFillFormMode: true },
              { animate: false }
            );
          } else {
            f7navigator.toPDFForm(document.id, null, { animate: false });
          }
          break;

        case DOC_TYPE_INFORMATION:
        case DOC_TYPE_IMMEDIATE_CONFIRMATION:
          f7navigator.toViewer(document.id, null, { animate: false });
          break;

        //no default
      }
    };

    navigateToMobileDocument = (document, backward, animate) => {
      gaClickOnDocType(document.type, document.id.substring(0, 4));

      beginDocument({
        'document.type': document.type,
        'document.id': document.id,
        'document.optional': Boolean(document.optional)
      });

      const docType = document.type;
      const navProps = {
        back: backward,
        ...(animate !== undefined && { animate })
      };

      switch (docType) {
        case DOC_TYPE_DOCUMENT:
          f7navigator.toGallery(document.id, {}, navProps);
          break;

        case DOC_TYPE_FORM:
          f7navigator.toQuickForm(document.id, {}, navProps);
          break;

        case DOC_TYPE_PAYMENT:
          f7navigator.toPayment(document.id, {}, navProps);
          break;

        case DOC_TYPE_PDF_FORM:
          if (this.needRedirectToSmartFill(document)) {
            f7navigator.toQuickForm(document.id, { smartFillFormMode: true });
          } else {
            f7navigator.toPDFForm(document.id, {}, navProps);
          }
          break;

        case DOC_TYPE_ID_CHECK:
          f7navigator.toIdCheck(document.id, {}, navProps);
          break;

        case DOC_TYPE_INFORMATION:
        case DOC_TYPE_IMMEDIATE_CONFIRMATION:
          f7navigator.toViewer(document.id, {}, navProps);
          break;

        //no default
      }
    };

    navigateToHome = () => {
      endDocument('ok', { 'document.end_reason': 'navigate_home' });
      gaeUserNavigate('Home');
      if (this.props.embedMode) {
        // Embed always sets skipStartPage=true. A real start/home page still
        // exists when skipStartPageOriginal is false (RequestStepper StartContent).
        // Only leave to the parent frame when there is no home page to return to.
        if (this.canExitEmbedToParent()) {
          this.notifyParentNavigateBack();
        } else {
          f7navigator.forceBackToList();
        }
        return;
      }
      if (window.isDesktop) {
        !this.props.skipStartPage &&
          f7navigator.forceBackToHome(null, { animate: false });
      } else if (this.props.skipStartPage || this.props.scanEnded) {
        this.onNavigateToScanQRClick(() =>
          f7navigator.toScanQR({
            returnFunction: () => this.navigateToRequestStart()
          })
        );
      } else {
        f7navigator.forceBackToHome();
      }
    };

    /**
     * Navigate to Scan QR, confirming only when unsaved input would be lost
     * and the standalone template draft is not cached in IndexedDB.
     * @param {function} [onNavigate] - optional; defaults to Scan QR with return to Home
     */
    onNavigateToScanQRClick = onNavigate => {
      const go =
        typeof onNavigate === 'function'
          ? onNavigate
          : () => {
              gaNavigateQrCode();
              f7navigator.toScanQR({
                returnFunction: () => f7navigator.toHome()
              });
            };

      const {
        scannedDocuments,
        forms,
        documentsStatus,
        rejectedDocuments,
        templateMode,
        recipientRequestId,
        scanEnded
      } = this.props;

      gaeApp('TRY_RETURN_TO_SCAN_QR');
      if (scanEnded) {
        go();
        return;
      }

      const hasChanges = hasAnyDocumentInputChanges(
        scannedDocuments,
        forms,
        documentsStatus,
        rejectedDocuments
      );

      if (!hasChanges) {
        go();
        return;
      }

      gaeApp('RETURN_TO_SCAN_QR.HAS_CHANGES');

      const confirmOrGo = shouldWarn => {
        gaeApp('SHOW_CONFIRM_DATA_LOOSE', shouldWarn);
        if (shouldWarn) {
          f7.dialog.confirm(
            II18.get('dialog_changies_will_removed'),
            II18.get('dialog_warning'),
            go
          );
        } else {
          go();
        }
      };

      if (window.draftEnabled && templateMode && recipientRequestId) {
        flushScheduledStandaloneDraftSave()
          .then(() => isStandaloneDraftCachedForTemplate(recipientRequestId))
          .then(cached => confirmOrGo(!cached))
          .catch(() => confirmOrGo(true));
        return;
      }

      confirmOrGo(true);
    };

    navigateToRequestStart = noanimate => {
      if (this.props.requestPages && !this.props.crossDeviceRequest) {
        if (window.isDesktop || this.props.scanEnded) {
          gaNavigateToList(true);
          f7navigator.toList({}, { animate: noanimate !== true }, true);
        } else {
          this.startMobileNextRequestPage(this.props.currentRequestPageIndex);
        }
      } else if (this.props.isSkipDocumentListMode) {
        const startDocument = this.props.requestedDocuments[
          this.props.startDocumentIndex
        ];
        gaeApp('NAVIGATE_DIRECT_TO_DOCUMENT', startDocument.type);
        window.isDesktop
          ? this.navigateToDesktopDocument(startDocument)
          : this.navigateToMobileDocument(
              startDocument,
              false,
              noanimate !== true
            );
      } else {
        gaNavigateToList(true);
        f7navigator.toList({}, { animate: noanimate !== true }, true);
      }
    };

    navigateBack = fromList => {
      if (this.props.crossDeviceRequest) {
        f7navigator.toScanQR({
          returnFunction: () => this.navigateToRequestStart()
        });
      } else if (this.props.onDocumentClose) {
        gaeApp('ON_DOCUMENT_CLOSE_ACTION');
        this.props.onDocumentClose();
      } else if (this.props.requestPages && !window.isDesktop) {
        this.navigateToRequestPrevPageFromMobileDocument(fromList === true);
      } else if (
        this.canExitEmbedToParent() &&
        (this.props.isSkipDocumentListMode || fromList === true)
      ) {
        this.notifyParentNavigateBack();
      } else if (this.props.isSkipDocumentListMode || fromList === true) {
        this.navigateToHome();
      } else {
        f7navigator.forceBackToList();
      }
    };

    documentCanNavigateBack() {
      // Embed: when no home/list/prev-page target remains, Back still exits to parent.
      const hasParentFrameReturn = Boolean(this.props.embedMode);
      const hasPageToReturn =
        !this.props.skipStartPage ||
        !this.props.isSkipDocumentListMode ||
        (this.props.requestPages && this.props.currentRequestPageIndex > 0);
      if (window.isDesktop) {
        return hasPageToReturn || hasParentFrameReturn;
      } else {
        return (
          hasPageToReturn ||
          hasParentFrameReturn ||
          window.standalone ||
          this.props.scanStarted === STARTED_FROM_QR
        );
      }
    }

    render() {
      const {
        currentActionForDocType,
        currentActionForDocId,
        currentActionForDocProps
      } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          navigateToRequestStart={this.navigateToRequestStart}
          documentCanNavigateBack={this.documentCanNavigateBack()}
          currentActionForDocProps={currentActionForDocProps}
          currentActionForDocType={currentActionForDocType}
          currentActionForDocId={currentActionForDocId}
          navigateBack={this.navigateBack}
          navigateToInPageDocument={this.navigateToInPageDocument}
          navigateToDesktopDocument={this.navigateToDesktopDocument}
          navigateToMobileDocument={this.navigateToMobileDocument}
          onNavigateToScanQRClick={this.onNavigateToScanQRClick}
          navigateToRequestNextPageFromMobileDocument={
            this.navigateToRequestNextPageFromMobileDocument
          }
          navigateToRequestPrevPageFromMobileDocument={
            this.navigateToRequestPrevPageFromMobileDocument
          }
          removeCurrentActionDocument={this.removeCurrentActionDocument}
          goToRequestPage={this.goToRequestPage}
          onSendRequest={this.onSendRequest}
          doSendRequestFromDocumentView={this.doSendRequestFromDocumentView}
          startCreateTemplateRequest={this.startCreateTemplateRequest}
        />
      );
    }
  }

  withDocumentRootLogicWrapper.propTypes = {
    startDocumentIndex: PropTypes.number,
    templateMode: PropTypes.bool,
    isSkipDocumentListMode: PropTypes.bool,
    currentRequestPageIndex: PropTypes.number,
    requestPages: PropTypes.any,
    requestedDocuments: PropTypes.array,
    preparedForms: PropTypes.object,
    preparedFormsStructures: PropTypes.object,
    scanEnded: PropTypes.bool,
    skipStartPage: PropTypes.bool,
    embedMode: PropTypes.bool,
    skipStartPageOriginal: PropTypes.bool,
    scanStarted: PropTypes.any,
    setCurrentRequestPageIndex: PropTypes.func,
    onDocumentClose: PropTypes.func,
    companyName: PropTypes.string,
    crossDeviceRequest: PropTypes.object,
    usersData: PropTypes.array,
    currentRecipientData: PropTypes.object,
    usersDataOk: PropTypes.bool,
    sendRequestTask: PropTypes.object,
    createTemplateRequestTask: PropTypes.object,
    templateRequestPrepared: PropTypes.bool,
    qesValueInheritance: PropTypes.object,
    embeddingFieldsInheritance: PropTypes.object,
    recipientConditions: PropTypes.array,
    startSendRequest: PropTypes.func,
    setSendRequestTask: PropTypes.func,
    setCreateTemplateRequestTask: PropTypes.func,
    setUserCredentialsTask: PropTypes.func,
    setUsersData: PropTypes.func,
    setDocumentForm: PropTypes.func,
    scannedDocuments: PropTypes.object,
    forms: PropTypes.object,
    documentsStatus: PropTypes.object,
    rejectedDocuments: PropTypes.object,
    recipientRequestId: PropTypes.string
  };

  const mapStateToProps = state => {
    const {
      requestedDocuments,
      requestPages,
      preparedFormsData,
      preparedFormsStructures
    } = selectPreparedState(state);

    const firstPageIsOneDocumentPage =
      !window.isDesktop &&
      requestPages &&
      requestPages[0]?.documents?.length === 1;

    const skipDocumentListMode =
      state.crossDeviceRequest !== null ||
      firstPageIsOneDocumentPage ||
      isSkipDocumentListMode(requestedDocuments, state.scanEnded);

    const requestPagesExt = requestPages && [
      ...requestPages,
      {
        title: II18.get('title_summary'),
        stepTitle: II18.get('title_done'),
        summaryStep: true,
        documents: []
      }
    ];

    const usersData = buildUsersData({
      usersData: state.usersData,
      requestedDocuments,
      preparedFormsData,
      preparedFormsStructures,
      qesValueInheritance: state.qesValueInheritance,
      embeddingFieldsInheritance: state.embeddingFieldsInheritance,
      recipientConditions: state.recipientConditions
    });

    return {
      startDocumentIndex: 0,
      needAesSignature: requestedDocuments.some(
        d => d.curentRecipientAdvancedSignaturesList?.length > 0
      ),
      isSkipDocumentListMode: skipDocumentListMode,
      rejectedDocuments: state.rejectedDocuments,
      scannedDocuments: state.scannedDocuments,
      forms: state.forms,
      documentsStatus: state.documentsStatus,
      templateMode: state.templateMode,
      recipientRequestId: state.recipientRequestId,
      preparedFormsData,
      preparedFormsStructures,
      manyPagesRequestNextButton:
        !isRequestACrossDeviceDocument(state.requestStartInfo) &&
        requestPagesExt &&
        requestPagesExt[state.currentRequestPageIndex].documents.length === 1,
      isManyPagesRequest: Array.isArray(state.requestPages),
      requestPages: requestPagesExt,
      autoSave: Boolean(requestPagesExt),
      requestedDocuments,
      currentRequestPageIndex: state.currentRequestPageIndex,
      skipStartPage: state.skipStartPage,
      embedMode: state.embedMode,
      skipStartPageOriginal: state.skipStartPageOriginal,
      scanEnded: state.scanEnded,
      scanStarted: state.scanStarted,
      rawRequestedDocuments: mapToArray(state.requestedDocuments),
      crossDeviceRequest: state.crossDeviceRequest,
      documentRejectionAllowed:
        state.documentRejectionAllowed && !state.templateMode,
      usersData,
      currentRecipientData: usersData.find(u => u.current),
      usersDataOk: usersDataAllOk(usersData),
      sendRequestTask: state.sendRequestTask,
      createTemplateRequestTask: state.createTemplateRequestTask,
      templateRequestPrepared: state.templateRequestPrepared,
      qesValueInheritance: state.qesValueInheritance,
      embeddingFieldsInheritance: state.embeddingFieldsInheritance,
      recipientConditions: state.recipientConditions
    };
  };

  const mapDispatchToProps = dispatch => ({
    showRejectDocumentDialog: documentId =>
      dispatch(showRejectDocumentDialog(documentId)),
    setCurrentRequestPageIndex: pageIndex =>
      dispatch(setCurrentRequestPageIndex(pageIndex)),
    startSendRequest: payload => dispatch(startSendRequest(payload)),
    setSendRequestTask: task => dispatch(setSendRequestTask(task)),
    setCreateTemplateRequestTask: task =>
      dispatch(setCreateTemplateRequestTask(task)),
    setUserCredentialsTask: task => dispatch(setUserCredentialsTask(task)),
    setUsersData: data => dispatch(setUsersData(data)),
    setDocumentForm: (documentId, form) =>
      dispatch(setDocumentForm(documentId, form))
  });

  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(withDocumentRootLogicWrapper);
}
